// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { Row, Column } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { PhoneCall, Headset, Mail, MapPin } from 'lucide-react';

import CookieConsent from 'Containers/CookieConsent/CookieConsent';
import { NETWORK_ONE_DISTRIBUTION } from 'Constants';

const StyledFooter = styled.div`
  padding: 20px 2rem;
  margin-top: 100px;
  background: linear-gradient(90deg, #040F2A 0%, #041B54 100%);
  color: #B2C2D5;
  stroke: #B2C2D5;
  line-height: 1.5rem;
  font-size: 0.875rem;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 21px;
    color: ${ props => props.theme.colors.white };
  }

  strong {
    color: #E7ECF2;
  }

  ul li {
    display: flex;
    align-items: center;
  }

  a {
    color: #FF8519;
    display: flex;
    align-items: center;
  }

  svg {
    stroke: #fff;
    margin-right: 10px;
    width: 16px;
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    padding: 16px 0;

    .cds--row {
      flex-direction: column;
    }
  }
`;

const StyledFooterColumn = styled.div`
  border-right: 1px solid #223F64;
  padding: 0px 20px;
  height: 100%;

  &.first {
    padding-left: 0;
  }

  &.last {
    padding-right: 0;
    border-right: none;
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    border: none;
    padding: 0;

    .sectionHeader {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 0;
      border-bottom: 1px solid #223F64;

      h3 {
        font-size: 16px;
        margin: 0;
      }
    }

    .sectionBody {
      padding: 16px;
    }
  }
`;

const StyledCopyright = styled.div`
  border-top: 1px solid #223F64;
  padding: 15px 0;
  margin: 30px 0 0 0;
  text-align: right;

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    text-align: center;
  }
`;

const Footer = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <>
      <StyledFooter theme={ theme }>
        <div className="grid-container">
          <Row>
            <Column>
              <StyledFooterColumn className="first">
                <div className="sectionHeader">
                  <h3>{ t('footer.hq') }</h3>
                </div>
                <div className="sectionBody">
                  <address>
                    <strong>{ t('footer.address') }</strong>: Str. Marcel Iancu, nr. 3-5, Bucuresti, 020757 (intersectie Mosilor - Dacia)
                  </address>
                  <ul>
                    <li><Headset /><span>+4021.211.18.56</span></li>
                    <li><PhoneCall /><span>+4021.212.10.10</span></li>
                    <li><PhoneCall /><span>+4021.212.12.50</span></li>
                    <li><Mail /><span>office@nod.ro</span></li>
                  </ul>
                  <a href="https://goo.gl/maps/ADrAq8KaD7P2" target="_blank"><MapPin />{ t('footer.viewMap') }</a>
                </div>
              </StyledFooterColumn>
            </Column>
            <Column>
              <StyledFooterColumn>
                <div className="sectionHeader">
                  <h3>{ t('footer.bank') }</h3>
                </div>
                <div className="sectionBody">
                  <ul>
                    <li><strong>{ t('footer.socialCapital') }</strong>: 5.750.000 RON</li>
                    <li><strong>{ t('footer.uniqueRegistrationCode') }</strong>: RO15056387</li>
                    <li><strong>{ t('footer.registrationNumber') }</strong>: J2012014309400</li>
                    <li><strong>{ t('footer.bank') }</strong>: RAIFFEISEN BANK</li>
                    <li><strong>{ t('footer.account') }</strong>: RO91 RZBR 0000 0600 1478 8636</li>
                  </ul>
                </div>
              </StyledFooterColumn>
            </Column>
            <Column>
              <StyledFooterColumn>
                <div className="sectionHeader">
                  <h3>{ t('footer.deposit') }</h3>
                </div>
                <div className="sectionBody">
                  <address><strong>{ t('footer.address') }</strong>: Parc logistic CTPark, depozit BUW10, Strada DC 149 nr. 2, comuna Bolintin Deal, judetul Giurgiu</address>
                  <ul>
                    <li><Headset /><span>+4021.211.18.56</span></li>
                    <li><PhoneCall /><span>+4021.212.10.10</span></li>
                    <li><PhoneCall /><span>+4021.212.12.50</span></li>
                    <li><Mail /><span>office@nod.ro</span></li>
                  </ul>
                  <a href="https://goo.gl/maps/3J2CHmLRSfP2" target="_blank"><MapPin />{ t('footer.viewMap') }</a>
                </div>
              </StyledFooterColumn>
            </Column>
            <Column>
              <StyledFooterColumn>
                <div className="sectionHeader">
                  <h3>{ t('footer.serviceAndWarranty') }</h3>
                </div>
                <div className="sectionBody">
                  <address><strong>{ t('footer.address') }</strong>: Parc logistic CTPark, depozit BUW10, Strada DC 149 nr. 2, comuna Bolintin Deal, judetul Giurgiu</address>
                  <ul>
                    <li><Headset /><span>+4021.211.18.56</span></li>
                    <li><PhoneCall /><span>+4021.212.10.10</span></li>
                    <li><PhoneCall /><span>+4021.212.12.50</span></li>
                    <li><Mail /><span>tehnic@nod.ro</span></li>
                  </ul>
                  <a href="https://goo.gl/maps/3J2CHmLRSfP2" target="_blank"><MapPin />{ t('footer.viewMap') }</a>
                </div>
              </StyledFooterColumn>
            </Column>
            <Column>
              <StyledFooterColumn className="last">
                <div className="sectionHeader">
                  <h3>{ t('footer.mdaDeposit') }</h3>
                </div>
                <div className="sectionBody">
                  <address><strong>{ t('footer.address') }</strong>: Parc logistic CTPark, depozit BUW14 (E4), Strada Ion Ratiu nr. 9, comuna Bolintin Deal, judetul Giurgiu</address>
                  <ul>
                    <li><Headset /><span>+4021.211.18.56</span></li>
                    <li><PhoneCall /><span>+4021.212.10.10</span></li>
                    <li><PhoneCall /><span>+4021.212.12.50</span></li>
                    <li><Mail /><span>office@nod.ro</span></li>
                  </ul>
                  <a href="https://goo.gl/maps/jyYU4v3zB2XiVHU19" target="_blank"><MapPin />{ t('footer.viewMap') }</a>
                </div>
              </StyledFooterColumn>
            </Column>
          </Row>
          <Row>
            <Column>
              <StyledCopyright><small>Copyright &copy; { `${ new Date().getFullYear() } ${ NETWORK_ONE_DISTRIBUTION }` }</small></StyledCopyright>
            </Column>
          </Row>
        </div>
      </StyledFooter>
      <CookieConsent />
    </>
  );
};

export default Footer;
